import { Progress } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSession } from "../../services/auth";
import { useAddGaAccountForNewUserMutation } from "../../services/authSlice";
import { OogwaiLogo2Full, OogwaiLogoFull } from "../../components/Icons/iconImages";

interface SignupLayoutProps {
    title: string | React.ReactNode;
    description: string | React.ReactNode;
    background?: string; // Can be an image URL or a CSS color
    icon: React.ReactNode;
    children?: React.ReactNode;
    percent: number;
    currentStep: number;
    routeState?: any;
    isProgressVisible?: boolean;
    isOogwaiHeader?: boolean;
}

const SignupLayout = ({
    title,
    description,
    background,
    icon,
    children,
    percent,
    currentStep,
    routeState,
    isProgressVisible = true,
    isOogwaiHeader = false,
}: SignupLayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const [addGaAccount] = useAddGaAccountForNewUserMutation();

    const [animate, setAnimate] = useState(false);
    const stepDurations = [120, 60, 0]; // 3 steps: verify-email, setup-workspace, set-up-connection
    const totalSteps = stepDurations.length;
    const stepPercent = 100 / totalSteps;
    const computedPercent = Math.min(currentStep * stepPercent, 100);
    const [animatedPercent, setAnimatedPercent] = useState(computedPercent);
    const prevPercentRef = useRef(computedPercent);
    const lastLocationKey = useRef(location.key);

    useEffect(() => {
        // Reset on location change
        if (lastLocationKey.current !== location.key) {
            lastLocationKey.current = location.key;
        }

        // Skip animation if same value
        if (Math.abs(computedPercent - prevPercentRef.current) < 0.1) {
            setAnimatedPercent(computedPercent);
            prevPercentRef.current = computedPercent;
            return;
        }

        let raf: number;
        const duration = 500; // Slightly longer for smoother animation
        const start = prevPercentRef.current;
        const end = computedPercent;
        const startTime = performance.now();

        // Easing function for smoother animation
        const easeInOutCubic = (t: number) => {
            return t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        function animateStep(now: number) {
            const elapsed = now - startTime;
            const rawProgress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(rawProgress);
            const value = start + (end - start) * easedProgress;

            setAnimatedPercent(Number(value.toFixed(1)));

            if (rawProgress < 1) {
                raf = requestAnimationFrame(animateStep);
            } else {
                setAnimatedPercent(end);
                prevPercentRef.current = end;
            }
        }

        raf = requestAnimationFrame(animateStep);
        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
    }, [computedPercent, location.key]);


    useEffect(() => {
        setTimeout(() => setAnimate(true), 100); // delay to trigger animations
    }, []);

    const handleClickAddGaAccount = async () => {
        try {
            const res = await addGaAccount('').unwrap();
        } catch (err) {
            console.error(err);
        }
    }

    function formatTime(seconds: number) {
        if (seconds <= 0) return '';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')} mins to complete`;
    }

    // Helper function to check if background is an image URL or color
    const isImageUrl = (value?: string) => {
        if (!value) return false;
        return value.startsWith('http') || value.startsWith('/') || value.startsWith('data:') || value.startsWith('.');
    };

    return (
        <section className="grid grid-cols-1 lg:grid-cols-2 h-screen">
            <div className="hidden lg:block relative w-full h-full">
                {isImageUrl(background) ? (
                    <img
                        src={background}
                        alt="Signup illustration"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="absolute inset-0 w-full h-full"
                        style={{ backgroundColor: background || '#3f6e57' }}
                    />
                )}
                <div className="relative z-10 flex flex-col h-full w-full px-8 py-8">
                    {isOogwaiHeader && (
                        <div className={`mx-auto opacity-0 ${animate ? 'animate-slideUp' : ''}`} style={{ animationDelay: '0.1s' }}>
                            <OogwaiLogoFull className="h-12" />
                        </div>
                    )}
                    <div className="flex items-center justify-center flex-1">
                        <div className="max-w-xl w-full space-y-4">
                            {icon && (
                                <div className={`opacity-0 ${animate ? 'animate-slideUp' : ''}`} style={{ animationDelay: '0.8s' }}
                                    onAnimationEnd={(e) => {
                                        e.currentTarget.classList.add('animate-pulseZoom');
                                    }}
                                >
                                    {icon}
                                </div>
                            )}
                            {title && <h1 className={`text-2xl font-semibold mb-2  opacity-0 ${animate ? 'animate-slideUp' : ''}`} style={{ animationDelay: '0.2s' }}>{title}</h1>}
                            {description && <div className={`text-base text-gray-700 opacity-0 ${animate ? 'animate-slideUp' : ''}`} style={{ animationDelay: '0.4s' }}>{description}</div>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col justify-start px-6 py-8 w-full h-full overflow-auto">
                {isProgressVisible && <Progress
                    percent={animatedPercent}
                    showInfo={false}
                    trailColor="#E9EAEB"
                    strokeColor="#609F81"
                    strokeLinecap="round"
                    className="transition-all duration-400 ease-in-out"
                />}

                {isProgressVisible && <div className="flex items-center justify-between">
                    <p className="text-[#717680] font-normal text-base mt-2">{currentStep}/{totalSteps}</p>
                    {(currentStep >= 2 && currentStep < totalSteps) && <p className="text-[#535862] font-normal text-sm text-center">
                        <span className="text-[#609F81] font-semibold cursor-pointer" onClick={async () => {
                            if (currentStep === totalSteps) {
                                try {
                                    handleClickAddGaAccount();
                                    const session: any = await getSession()
                                    const token = session.idToken.jwtToken;
                                    const user = state?.header?.Authorization?.split(' ')[1];
                                    if (token || user) {
                                        if (user) {
                                            localStorage.setItem("oogw.ai_user", user)
                                            navigate('/dashboard')
                                        } else {
                                            localStorage.setItem("oogw.ai_user", token)
                                            navigate('/dashboard')
                                        }
                                    } else {
                                        navigate('/login')
                                    }
                                } catch (error) {
                                    navigate('/login')
                                }
                            } else {
                                navigate(
                                    currentStep === 2 ? '/set-up-connection' : '/dashboard',
                                    {
                                        state: routeState
                                    }
                                )
                            }
                        }}>Skip</span></p>
                    }
                </div>}

                {children}
            </div>
        </section>
    );
};

export default SignupLayout;
